// OPA-Bundles format adapter API client (roadmap item 45).
//
// The OPA adapter mounts under `/opa/<project>/<repo>/`. The two helpers
// below sit on the JSON-shaped catalog endpoints — list of bundles + per-
// bundle detail with parsed Rego/data preview — that the OPABundles page
// renders.
//
// Slugs come from the repo's project + repo slug pair (UI converts a UUID
// `Repo` from `useRepoDetail` via `useProjects()`).

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

export interface OPABundleSummary {
  name: string;
  version: string;
  revision: string;
  digest: string;
  size_bytes: number;
  rego_files: number;
  roots_count: number;
  data_paths_count: number;
  parsed_ok: boolean;
  uploaded_at: string;
}

export interface OPABundleListResponse {
  project: string;
  repo: string;
  bundles: OPABundleSummary[];
  pull_hint: string;
}

export interface OPARegoFile {
  path: string;
  size: number;
  body: string;
  truncated: boolean;
}

export interface OPADataDoc {
  path: string;
  size: number;
  body: string;
}

export interface OPABundleDetailResponse {
  project: string;
  repo: string;
  name: string;
  version: string;
  digest: string;
  size_bytes: number;
  uploaded_at: string;
  metadata: {
    name?: string;
    revision?: string;
    roots?: string[];
    rego_files?: number;
    data_paths?: string[];
    parsed_ok?: boolean;
    [key: string]: unknown;
  };
  rego: OPARegoFile[];
  data: OPADataDoc[];
  manifest_raw: string;
  pull_url: string;
}

const fetcher = async (url: string) => {
  const r = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

export function useOPABundles(projectSlug?: string, repoSlug?: string) {
  const url =
    projectSlug && repoSlug
      ? `/opa/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/bundles`
      : null;
  return useSWR<OPABundleListResponse>(url, fetcher);
}

export function useOPABundleDetail(
  projectSlug: string | undefined,
  repoSlug: string | undefined,
  name: string | undefined,
  version: string | undefined,
) {
  const url =
    projectSlug && repoSlug && name && version
      ? `/opa/${encodeURIComponent(projectSlug)}/${encodeURIComponent(repoSlug)}/bundles/${encodeURIComponent(name)}/${encodeURIComponent(version)}`
      : null;
  return useSWR<OPABundleDetailResponse>(url, fetcher);
}
